import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import type { BoardCard as Card, FlystStage } from '@/api/schema';
import { api } from '@/api/client';
import { Chip } from '@/components/ui/Chip';
import { Drawer } from '@/components/ui/Drawer';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { CANDIDATES_KEY } from '@/features/candidates/api';
import { Fact, Facts, FactText } from '@/features/candidates/components/CandidateFacts';
import { PointsMeter } from '@/features/candidates/components/PointsMeter';
import { FLYST_LABEL, FLYST_OPTIONS, FLYST_TONE } from '@/features/candidates/labels';
import { formatDateTime, type DateFormat } from '@/lib/dates';

import { ScreeningNotes } from './ScreeningNotes';

import styles from './ScreeningDrawer.module.css';

type StageEvent = {
  id: string;
  from_value: string | null;
  to_value: string;
  note: string | null;
  occurred_at: string;
  changed_by: { id: string; full_name: string } | null;
};

/**
 * One candidate's screening.
 *
 * Distinct from their record, which is a whole page and reachable from the name on the
 * card: this answers "what is happening with this screening" and nothing else, so it is
 * a drawer over the board rather than a navigation away from it. Losing your place on a
 * four-column board to read one card would be a poor trade.
 */
export function ScreeningDrawer({
  card,
  dateFormat,
  moving,
  onClose,
  onMove,
}: {
  card: Card | null;
  dateFormat: DateFormat;
  moving: boolean;
  onClose: () => void;
  onMove: (card: Card, stage: FlystStage) => void;
}) {
  const history = useQuery({
    queryKey: [...CANDIDATES_KEY, 'history', card?.id, 'flyst_stage'],
    queryFn: () =>
      api.get<StageEvent[]>(`/api/v1/candidates/${card?.id ?? ''}/history`, {
        query: { field: 'flyst_stage' },
      }),
    enabled: card !== null,
  });

  if (!card) return null;

  return (
    <Drawer
      open
      onClose={onClose}
      title={card.full_name}
      description={`${card.reference} · Flyst screening`}
    >
      <div className={styles.stage}>
        <Chip tone={FLYST_TONE[card.flyst_stage]}>{FLYST_LABEL[card.flyst_stage]}</Chip>
        <span className={styles.waiting}>
          {card.waiting_days === 0
            ? 'Moved here today'
            : `${card.waiting_days} ${card.waiting_days === 1 ? 'day' : 'days'} in this stage`}
        </span>
      </div>

      <label className={styles.moveLabel}>
        <span className={styles.moveText}>Move to</span>
        <Select
          value={card.flyst_stage}
          disabled={moving}
          onChange={(event) => {
            onMove(card, event.target.value as FlystStage);
          }}
          options={FLYST_OPTIONS}
        />
      </label>

      <Facts>
        <Fact label="Position">
          <FactText value={card.preferred_position?.name} />
        </Fact>
        <Fact label="Department">
          <FactText value={card.department?.name} />
        </Fact>
        <Fact label="Destination">
          <FactText value={card.preferred_country?.name} />
        </Fact>
        <Fact label="Sponsor points">
          <PointsMeter value={card.sponsor_points} />
        </Fact>
      </Facts>

      <h3 className={styles.heading}>How it got here</h3>
      {history.isPending ? (
        <p className={styles.empty}>
          <Spinner size={14} label="Loading the history" />
        </p>
      ) : history.isError ? (
        <p className={styles.empty}>That history did not load.</p>
      ) : (
        <ol className={styles.history}>
          {history.data.map((event) => (
            <li key={event.id} className={styles.event}>
              <span className={styles.change}>
                {event.from_value === null ? (
                  <>
                    Started at <b>{stageLabel(event.to_value)}</b>
                  </>
                ) : (
                  <>
                    {stageLabel(event.from_value)} <span aria-hidden="true">→</span>{' '}
                    <b>{stageLabel(event.to_value)}</b>
                  </>
                )}
              </span>
              <span className={styles.meta}>
                {formatDateTime(event.occurred_at, dateFormat)}
                {event.changed_by ? ` · ${event.changed_by.full_name}` : ''}
              </span>
              {event.note ? <span className={styles.note}>{event.note}</span> : null}
            </li>
          ))}
        </ol>
      )}

      <ScreeningNotes candidateId={card.id} kind="flyst" dateFormat={dateFormat} />

      <Link className={styles.record} to={`/candidates/${card.id}`}>
        Open the full record
      </Link>
    </Drawer>
  );
}

/** Tolerates a stage no longer in the list (history outlives the enum). */
function stageLabel(value: string): string {
  return (FLYST_LABEL as Record<string, string | undefined>)[value] ?? value;
}
